/**
 * SettingsPage — tenant settings + integrations governance.
 * Integration management requires `integration:manage` (Owner/Admin/Platform Root).
 * Lower roles see the list but all edit controls are hidden.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import AppHeader from './layout/AppHeader';
import LLMBudgetPanel from './LLMBudgetPanel';
import PlanFeaturesPanel from './PlanFeaturesPanel';
import UsagePanel from './UsagePanel';
import PasswordPolicyPanel from './PasswordPolicyPanel';
import { useAuth } from '../context/AuthContext';
import { apiErrorFromResponse, messageFor, formatBackendDetail } from '../lib/apiErrors';
import { tenantBasePath } from '../lib/tenantPath';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_CHECK_DEMO_REFERENCE = 'finder-foundry-client-demo-readiness-v1';
const terminalProviderCheck = (value) => ['COMPLETED', 'UNKNOWN', 'BLOCKED'].includes(value?.status);
const boundedProviderWait = async (work) => {
  let timer;
  try { return await Promise.race([work, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Outcome unknown — inspect the same existing check.')), 15000); })]); }
  finally { clearTimeout(timer); }
};

function ProviderCheckConfirmation({ value, onCancel, onConfirm }) {
  const panel = useRef(null), cancel = useRef(null);
  useEffect(() => {
    cancel.current?.focus();
    const trap = (event) => { if (panel.current && !panel.current.contains(event.target)) cancel.current?.focus(); };
    document.addEventListener('focusin', trap);
    return () => { document.removeEventListener('focusin', trap); if (value.origin?.isConnected) value.origin.focus(); };
  }, [value]);
  return createPortal(<div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: '#0008', display: 'grid', placeItems: 'center', padding: 16 }}>
    <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="provider-check-title" data-testid="provider-check-confirmation"
      style={{ background: 'var(--surface,white)', borderRadius: 8, padding: 20, maxWidth: 520 }} onKeyDown={(event) => {
        if (event.key === 'Escape') { event.preventDefault(); onCancel(); }
        if (event.repeat && ['Enter', ' '].includes(event.key)) event.preventDefault();
        if (event.key === 'Tab') { const buttons = [...panel.current.querySelectorAll('button')]; if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons[1].focus(); } else if (!event.shiftKey && document.activeElement === buttons[1]) { event.preventDefault(); buttons[0].focus(); } }
      }}>
      <h3 id="provider-check-title">Confirm bounded provider generation check</h3>
      <p>Tenant: {value.tenant_id}. Up to three sequential generation requests, one per configured baseline provider; 4,096 total output tokens each. No fallback, SDK retry, search or tools.</p>
      <p>This returns an illustrative public workflow—not researched customer evidence or an end-to-end failover test. Charges and unknown liability are retained.</p>
      {value.task_reference && <p>Client-demo reference: {value.task_reference}. Shared direct-check allocation: $1 within the existing $10 total—not a renewed allowance.</p>}
      <button ref={cancel} className="btn btn-outline" style={{ minHeight: 44 }} onClick={onCancel}>Cancel</button>
      <button className="btn btn-primary" style={{ minHeight: 44, marginLeft: 8 }} onClick={onConfirm}>Confirm provider check</button>
    </div>
  </div>, document.body);
}

export function ProviderHealthPanel() {
  const { user, hasPermission } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [journal, setJournal] = useState(null);
  const [recoveryBlocked, setRecoveryBlocked] = useState(false);
  const [demoScope, setDemoScope] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const tenantRef = useRef(null);
  const loadSequence = useRef(0);
  const canManage = !!user?.is_platform_root || !!hasPermission?.('integration:manage');
  const journalKey = (tenant) => `finder-provider-check-v1:${tenant}`;
  const scopedHeaders = (tenant) => user?.is_platform_root ? { 'X-Active-Tenant-Id': tenant } : {};
  const readResult = async (response) => {
    if (!response.ok) throw await apiErrorFromResponse(response, { context: 'health' });
    return response.json();
  };

  const load = async () => {
    const sequence = ++loadSequence.current;
    const previousTenant = tenantRef.current;
    tenantRef.current = null; // A refreshed scope must be verified before charging.
    setConfirmation(null);
    setLoading(true); setError(null);
    try {
      const res = await boundedProviderWait(fetch(`${API_URL}/api/settings/health`));
      if (!mounted.current || sequence !== loadSequence.current) return;
      if (res.status === 403 || res.status === 401) {
        // Hide silently for non-admin viewers — panel is admin-only.
        setHealth(null); setError('forbidden'); return;
      }
      if (!res.ok) throw await apiErrorFromResponse(res, { context: 'health' });
      const next = await boundedProviderWait(readResult(res));
      if (!mounted.current || sequence !== loadSequence.current) return;
      tenantRef.current = next.tenant_id;
      setHealth(next);
      if (previousTenant !== next.tenant_id) { setProbe(null); setDemoScope(false); }
      try {
        const saved = JSON.parse(sessionStorage.getItem(journalKey(next.tenant_id)) || 'null');
        if (saved !== null && (!/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(saved.request_id || '') || saved.tenant_id !== next.tenant_id || (saved.task_reference && saved.task_reference !== PROVIDER_CHECK_DEMO_REFERENCE))) throw new Error('Invalid recovery journal');
        setJournal(saved ? { request_id: saved.request_id, tenant_id: saved.tenant_id, ...(saved.task_reference ? { task_reference: saved.task_reference } : {}) } : null);
        setRecoveryBlocked(false);
      } catch {
        setJournal(null); setRecoveryBlocked(true);
        setProbe({ error: 'Recovery journal unavailable; do not repeat an uncertain generation. Preserve its records and inspect the existing operation before a new check.' });
      }
    } catch (e) { if (mounted.current && sequence === loadSequence.current) setError(messageFor(e, { context: 'health' })); }
    finally { if (mounted.current && sequence === loadSequence.current) setLoading(false); }
  };

  const inspectProbe = async (record = journal) => {
    if (!record || busy.current) return;
    busy.current = true; setProbing(true);
    try {
      const data = await boundedProviderWait(fetch(`${API_URL}/api/settings/health?provider_test_request_id=${encodeURIComponent(record.request_id)}`, { headers: scopedHeaders(record.tenant_id) }).then(readResult));
      if (mounted.current && tenantRef.current === record.tenant_id) setProbe(data);
    } catch { if (mounted.current && tenantRef.current === record.tenant_id) setProbe({ error: 'Outcome unknown — inspect this same existing check. Do not submit another.', status: 'UNKNOWN' }); }
    finally { busy.current = false; if (mounted.current) setProbing(false); }
  };

  const runProbe = (event) => {
    if (busy.current || loading || recoveryBlocked || !canManage || !health?.tenant_id || tenantRef.current !== health.tenant_id) return;
    if (journal) { inspectProbe(journal); return; }
    const request_id = globalThis.crypto?.randomUUID?.();
    if (!request_id) { setProbe({ error: 'A secure request identity is unavailable; no request was sent.' }); return; }
    setConfirmation({ request_id, tenant_id: health.tenant_id, task_reference: user?.is_platform_root && demoScope ? PROVIDER_CHECK_DEMO_REFERENCE : undefined, origin: event.currentTarget });
  };

  const confirmProbe = async () => {
    const frozen = confirmation;
    if (!frozen || busy.current || loading || recoveryBlocked || tenantRef.current !== frozen.tenant_id || !canManage) { setConfirmation(null); return; }
    busy.current = true;
    setConfirmation(null);
    const record = { request_id: frozen.request_id, tenant_id: frozen.tenant_id, ...(frozen.task_reference ? { task_reference: frozen.task_reference } : {}) };
    try { sessionStorage.setItem(journalKey(record.tenant_id), JSON.stringify(record)); }
    catch { busy.current = false; setProbe({ error: 'Cannot preserve the request identity; no generation was sent.' }); return; }
    setJournal(record);
    setProbing(true);
    try {
      const data = await boundedProviderWait(fetch(`${API_URL}/api/settings/health/test-providers`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...scopedHeaders(record.tenant_id) }, body: JSON.stringify({ request_id: record.request_id, task_reference: record.task_reference }),
      }).then(readResult));
      if (mounted.current && tenantRef.current === record.tenant_id) setProbe(data);
    } catch (e) {
      if (mounted.current && tenantRef.current === record.tenant_id) setProbe({ error: 'Outcome unknown — inspect this same existing check. Do not submit another.', status: 'UNKNOWN' });
    } finally {
      busy.current = false;
      if (mounted.current) setProbing(false);
    }
  };

  useEffect(() => { mounted.current = true; load(); return () => { mounted.current = false; }; }, []);
  useEffect(() => { setConfirmation(null); }, [health?.tenant_id]);
  if (error === 'forbidden') return null;

  const sourceTone = (s) =>
    s === 'env' ? '#15803d' :
    s === 'db_override' ? '#1d4ed8' :
    s === 'official_no_key' ? '#15803d' :
    s === 'decrypt_error' ? '#b91c1c' :
    s === 'not_configured' ? '#b45309' :
    s === 'no_user_agent' ? '#b45309' : 'var(--text-secondary)';

  const sourceLabel = (s) =>
    s === 'env' ? 'environment' :
    s === 'db_override' ? 'admin override' :
    s === 'official_no_key' ? 'official · no key' :
    s === 'decrypt_error' ? 'decrypt error — admin attention' :
    s === 'not_configured' ? 'not configured' :
    s === 'no_user_agent' ? 'user-agent missing' : s;

  return (
    <div className="panel" style={{ marginBottom: 16 }} data-testid="provider-health-panel">
      <div className="panel-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', margin: 0 }}>
            Provider health
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {health?.status && (
              <span data-testid="provider-health-status" style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                background: health.status === 'ok' ? '#dcfce7' : '#fef3c7',
                color: health.status === 'ok' ? '#15803d' : '#92400e',
              }}>{health.status === 'ok' ? 'All providers configured' : 'Degraded'}</span>
            )}
            <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}
                    data-testid="provider-health-refresh">
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            {canManage && <button className="btn btn-secondary btn-sm" onClick={runProbe} disabled={loading || recoveryBlocked || probing || !health?.tenant_id}
                    title="A bounded generation check, not configuration-only health or an end-to-end failover test"
                    data-testid="provider-health-test-providers">
              {probing ? 'Checking existing operation…' : journal ? 'Inspect existing check' : 'Test providers'}
            </button>}
          </div>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
          Operational view from <code>/api/settings/health</code>. Status only — no key material is exposed.
        </p>
        {canManage && !health?.tenant_id && <p role="status">Select an authorised tenant in Platform before any charged provider check.</p>}
        {user?.is_platform_root && <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          <input type="checkbox" checked={demoScope} disabled={!!journal || probing} onChange={(event) => setDemoScope(event.target.checked)} data-testid="provider-check-demo-scope" />
          {' '}Use the existing client-demo direct-check allocation ($1 within its $10 total).
        </label>}
        {journal && <p style={{ fontSize: 12 }} data-testid="provider-check-existing-identity">Existing check {journal.request_id}. Reopening or inspecting does not generate again.</p>}
        {canManage && journal && probe?.status === 'NOT_STARTED' && <button className="btn btn-outline" disabled={probing} onClick={(event) => setConfirmation({ ...journal, origin: event.currentTarget })} data-testid="provider-check-resume-unstarted">Confirm the same unstarted check</button>}
        {loading && !health && <div style={{ fontSize: 12 }}>Checking provider health…</div>}
        {error && error !== 'forbidden' && (
          <div style={{ fontSize: 12, color: 'var(--danger, #dc2626)' }}>{error}</div>
        )}
        {health && (
          <div style={{ display: 'grid', gap: 4 }}>
            {(health.providers || []).map((p) => (
              <div key={p.key || p.name} data-testid={`provider-health-${p.name}`}
                   style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '4px 0', borderBottom: '1px solid var(--border-subtle, #eee)', fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>
                  {p.name}
                  {p.alias && (
                    <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-secondary)' }}>
                      via {p.alias}
                    </span>
                  )}
                </span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: sourceTone(p.source) }}>{sourceLabel(p.source)}</span>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: p.configured ? '#16a34a' : '#d97706',
                  }} />
                </span>
              </div>
            ))}
            {health.timestamp && (
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 6 }}>
                Checked {new Date(health.timestamp).toLocaleString()}
              </div>
            )}
          </div>
        )}
        {probe && (
          <div data-testid="provider-probe-results"
               style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-subtle, #eee)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                          letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
              Direct baseline adapter acceptance — not researched/customer evidence
            </div>
            {probe.error ? (
              <div style={{ fontSize: 12, color: 'var(--danger, #dc2626)' }}>{probe.error}</div>
            ) : (
              <div style={{ display: 'grid', gap: 4 }}>
                <p role="status">Operation: {probe.status}. No fallback or SDK retry. {terminalProviderCheck(probe) ? 'Preserved result; inspect or reopen without regenerating.' : 'Inspect this same operation if it is still running or uncertain.'}</p>
                {probe.error_type && <p data-testid="provider-check-safe-failure">Retained failure: {probe.error_type}{probe.execution_diagnostic?.code ? ` · ${probe.execution_diagnostic.stage}/${probe.execution_diagnostic.code}` : ' · specific historical cause was not retained'}. No replacement check has been created.</p>}
                {probe.accounting && <p style={{ fontSize: 12 }}>Occupied ${Number(probe.accounting.occupied_usd).toFixed(6)} · recorded estimate ${Number(probe.accounting.recorded_estimate_usd).toFixed(6)} · outstanding ${Number(probe.accounting.outstanding_reservation_usd).toFixed(6)} · unresolved ${Number(probe.accounting.unresolved_liability_usd).toFixed(6)}. Not vendor-billing reconciliation.</p>}
                {(probe.results || []).map((r) => {
                  const tone =
                    r.status === 'ok' ? '#15803d' :
                    r.status === 'not_configured' ? 'var(--text-secondary)' :
                    r.status === 'quota_exceeded' || r.status === 'auth_failed' ? '#b45309' :
                    '#dc2626';
                  return (
                    <div key={r.provider}
                         data-testid={`provider-probe-${r.provider}`}
                         style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap',
                                  alignItems: 'center', fontSize: 12,
                                  padding: '4px 0',
                                  borderBottom: '1px solid var(--border-subtle, #eee)' }}>
                      <span style={{ fontWeight: 500 }}>
                        {r.provider}
                        <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-secondary)' }}>
                          {r.model}
                        </span>
                      </span>
                      {r.output && <div style={{ flexBasis: '100%', paddingTop: 8 }} data-testid={`provider-check-output-${r.provider}`}>
                        <strong>{r.output.title}</strong><ol>{r.output.steps.map((step, index) => <li key={index}>{step}</li>)}</ol>
                        <p>{r.output.limitation}</p><small>Illustrative public workflow. No real organisation researched; not a Foundry decision or customer assessment.</small>
                      </div>}
                      <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {r.latency_ms != null && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                            {r.latency_ms} ms
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: tone, fontWeight: 600 }}>
                          {r.status}
                        </span>
                      </span>
                    </div>
                  );
                })}
                {probe.tested_at && (
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 6 }}>
                    Tested {new Date(probe.tested_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {confirmation && <ProviderCheckConfirmation value={confirmation} onCancel={() => setConfirmation(null)} onConfirm={confirmProbe} />}
      </div>
    </div>
  );
}


function IntegrationRow({ integration, canManage, onSave, onClear, onRemoveCustom }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(null); // 'save' | 'clear' | null
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Resilience to old + new payload shapes. Resolver returns `key` as canonical
  // env-var name; legacy custom-integration response had `env`.
  const envKey = integration.env || integration.key;
  const isTenantOverride = integration.source === 'tenant_override';
  const isGlobalOverride = integration.source === 'db_override';
  const isOverride = isTenantOverride || isGlobalOverride;

  // Iter 38f — source labels distinguish tenant overrides from platform
  // defaults so the operator always knows what tier is in effect.
  const sourceLabel =
    isTenantOverride ? 'tenant override' :
    isGlobalOverride ? 'platform default' :
    integration.source === 'env' ? 'environment' :
    integration.source === 'not_configured' || integration.source === 'unset' ? 'not configured' :
    integration.source;

  const requestSave = () => {
    if (!value.trim()) return;
    if (integration.configured) {
      setConfirming('save');  // overwriting an active value — confirm first
      return;
    }
    doSave();
  };

  const doSave = async () => {
    setBusy(true); setError(null); setConfirming(null);
    try {
      await onSave(envKey, value.trim());
      setValue(''); setEditing(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const requestClear = () => setConfirming('clear');

  const doClear = async () => {
    setBusy(true); setError(null); setConfirming(null);
    try {
      await onClear(envKey);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="panel" style={{ marginBottom: 8 }} data-testid={`integration-${envKey}`}>
      <div className="panel-body" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px', minWidth: 220 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {integration.label}
            {integration.aliases && integration.aliases.length > 0 && (
              <span className="badge badge-type" style={{ marginLeft: 6, fontSize: 9 }}>
                ALIAS: {integration.aliases.join(', ')}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            {integration.description}
          </div>
        </div>
        <div style={{ flex: '1 1 180px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
          {integration.configured ? (
            <span title="Configured (value masked)">
              {integration.masked_value || 'configured, hidden'}
            </span>
          ) : (
            <span style={{ color: 'var(--danger)' }}>not configured</span>
          )}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', width: 140 }}>
          {sourceLabel}
          {integration.aliased_from && (
            <div style={{ textTransform: 'none', letterSpacing: 0, fontSize: 10, marginTop: 2 }}>
              via {integration.aliased_from}
            </div>
          )}
          {integration.last_updated_by && isOverride && (
            <div style={{ textTransform: 'none', letterSpacing: 0, fontSize: 10, marginTop: 2 }}>
              by {integration.last_updated_by}
            </div>
          )}
        </div>

        {canManage && !editing && !confirming && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn btn-outline btn-sm"
              data-testid={`integration-edit-${envKey}`}
              onClick={() => setEditing(true)}
            >
              {isOverride ? 'Rotate' : 'Override'}
            </button>
            {isOverride && (
              <button
                className="btn btn-outline btn-sm"
                data-testid={`integration-clear-${envKey}`}
                onClick={requestClear}
              >
                Clear override
              </button>
            )}
            {integration.builtin === false && (
              <button
                className="btn btn-outline btn-sm"
                data-testid={`integration-remove-${envKey}`}
                onClick={() => onRemoveCustom(envKey, integration.label)}
                style={{ color: 'var(--danger)' }}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>

      {editing && !confirming && (
        <div className="panel-body" style={{ borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="password"
            placeholder={`New value for ${integration.label}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid={`integration-input-${envKey}`}
            autoComplete="new-password"
            style={{ flex: '1 1 280px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}
          />
          <button className="btn btn-primary btn-sm" onClick={requestSave} disabled={busy || !value.trim()} data-testid={`integration-save-${envKey}`}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => { setEditing(false); setValue(''); setError(null); }}>
            Cancel
          </button>
          {error && <div className="auth-error" style={{ width: '100%' }}>{error}</div>}
        </div>
      )}

      {confirming === 'save' && (
        <div className="panel-body" style={{ borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap', background: 'var(--surface-2)' }}>
          <div style={{ width: '100%', fontSize: 12, color: 'var(--text-secondary)' }}>
            <strong>Overwrite active {integration.label} value?</strong> The current value will be replaced.
            This action is audit-logged.
          </div>
          <button className="btn btn-primary btn-sm" onClick={doSave} disabled={busy} data-testid={`integration-confirm-save-${envKey}`}>
            {busy ? 'Saving…' : 'Yes, overwrite'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setConfirming(null)} disabled={busy}>
            Cancel
          </button>
        </div>
      )}

      {confirming === 'clear' && (
        <div className="panel-body" style={{ borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap', background: 'var(--surface-2)' }}>
          <div style={{ width: '100%', fontSize: 12, color: 'var(--text-secondary)' }}>
            <strong>Clear admin override for {integration.label}?</strong> The system will fall back to the
            environment variable. This action is audit-logged.
          </div>
          <button className="btn btn-primary btn-sm" onClick={doClear} disabled={busy} data-testid={`integration-confirm-clear-${envKey}`}>
            {busy ? 'Clearing…' : 'Yes, clear override'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setConfirming(null)} disabled={busy}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Iter 38f — Tenant-scoped provider overrides panel ─────────────────────
//
// Only rendered when the URL is `/t/<slug>/settings`. Calls the
// NEW `/api/settings/tenant-integrations` routes — NEVER touches
// the global `/api/settings/integrations` endpoint, so a tenant
// save cannot accidentally overwrite the platform default.
function TenantIntegrationsPanel({ canManage }) {
  const [integrations, setIntegrations] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/settings/tenant-integrations`);
      if (!res.ok) throw await apiErrorFromResponse(res, { context: 'tenant-integrations' });
      const data = await res.json();
      setIntegrations(data.integrations || []);
      setTenantId(data.tenant_id || null);
    } catch (e) {
      setError(messageFor(e, { context: 'tenant-integrations' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveTenantOverride = async (envKey, value) => {
    const res = await fetch(`${API_URL}/api/settings/tenant-integrations/${envKey}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatBackendDetail(err.detail) || 'Failed to save tenant override');
    }
    await load();
  };

  const clearTenantOverride = async (envKey) => {
    const res = await fetch(`${API_URL}/api/settings/tenant-integrations/${envKey}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setError(formatBackendDetail(err.detail) || 'Failed to clear tenant override');
      return;
    }
    await load();
  };

  return (
    <div style={{ marginBottom: 24 }} data-testid="tenant-integrations-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', margin: 0 }}>
          Tenant overrides
        </h3>
        {tenantId && (
          <span
            data-testid="tenant-integrations-tenant-id"
            style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Tenant: {tenantId}
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
        Configure provider keys for this tenant only. Tenant overrides take
        precedence over the platform defaults below. When no tenant override
        exists, the resolver falls back to the platform default and finally
        to the environment variable.
      </p>
      {loading && <div className="panel"><div className="panel-body">Loading tenant overrides…</div></div>}
      {error && <div className="auth-error" data-testid="tenant-integrations-error">{error}</div>}
      {!loading && !error && integrations.map((i) => (
        <IntegrationRow
          key={`tenant-${i.key}`}
          integration={i}
          canManage={canManage}
          onSave={saveTenantOverride}
          onClear={clearTenantOverride}
          onRemoveCustom={() => {}}
        />
      ))}
    </div>
  );
}


export default function SettingsPage() {
  const location = useLocation();
  const tenantBase = tenantBasePath(location.pathname);
  const isTenantContext = Boolean(tenantBase);
  const { hasPermission } = useAuth();
  const canManage = hasPermission('integration:manage');
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/settings/integrations`);
      if (!res.ok) throw await apiErrorFromResponse(res, { context: 'integrations' });
      const data = await res.json();
      setIntegrations(data.integrations || []);
    } catch (e) { setError(messageFor(e, { context: 'integrations' })); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveIntegration = async (envKey, value) => {
    const res = await fetch(`${API_URL}/api/settings/integrations/${envKey}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatBackendDetail(err.detail) || 'Failed to save');
    }
    await load();
  };

  const clearIntegration = async (envKey) => {
    const res = await fetch(`${API_URL}/api/settings/integrations/${envKey}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setError(formatBackendDetail(err.detail) || 'Failed to clear');
      return;
    }
    await load();
  };

  const [pendingRemove, setPendingRemove] = useState(null); // {key, label}
  const confirmRemoveCustom = (envKey, label) => setPendingRemove({ key: envKey, label });
  const executeRemoveCustom = async () => {
    if (!pendingRemove) return;
    const res = await fetch(`${API_URL}/api/settings/integrations/custom/${pendingRemove.key}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setError(formatBackendDetail(err.detail) || 'Failed to remove');
    }
    setPendingRemove(null);
    await load();
  };

  // Add-integration dialog state
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ label: '', key: '', description: '', value: '' });
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);

  const resetAdd = () => {
    setAddForm({ label: '', key: '', description: '', value: '' });
    setAddError(null);
  };

  const submitAdd = async () => {
    setAddBusy(true); setAddError(null);
    try {
      const body = {
        key: addForm.key.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
        label: addForm.label.trim(),
        description: addForm.description.trim(),
      };
      if (addForm.value.trim()) body.value = addForm.value.trim();
      if (!body.label || body.label.length < 2) { setAddError('Label is required'); return; }
      if (!body.key || body.key.length < 2) { setAddError('Key is required'); return; }

      const res = await fetch(`${API_URL}/api/settings/integrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(err.detail) || 'Failed to add');
      }
      setShowAdd(false);
      resetAdd();
      await load();
    } catch (e) { setAddError(e.message); }
    finally { setAddBusy(false); }
  };

  return (
    <>
      <AppHeader active="settings" />
      <div className="page-shell" data-testid="settings-page">
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: '8px 0 6px' }}>Settings</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Manage integrations, LLM budget and tenant preferences. Only Admins and above can add or rotate keys.
        </p>

        {/* Plan & features (read-only, all roles can view their plan) */}
        <PlanFeaturesPanel />

        {/* Usage & limits (admin-only; silent for Operators/Viewers) */}
        <UsagePanel />

        {/* LLM Budget (Owner/Admin only; hidden otherwise) */}
        <LLMBudgetPanel />

        {/* Password policy (Owner/Admin only; off-by-default) */}
        <PasswordPolicyPanel />

        {/* Provider health — operational at-a-glance, polls /api/settings/health */}
        <ProviderHealthPanel />

        {/* Iter 38f — Tenant Overrides panel: only rendered inside a
            tenant URL (/t/<slug>/settings). Calls the dedicated
            /api/settings/tenant-integrations endpoint so a tenant
            save can NEVER overwrite the platform default. */}
        {isTenantContext && <TenantIntegrationsPanel canManage={canManage} />}

        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', margin: 0 }}>
              {isTenantContext ? 'Platform defaults (inherited)' : 'Integrations'}
            </h3>
            {canManage && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { resetAdd(); setShowAdd(true); }}
                data-testid="integration-add-btn"
              >
                + Add integration
              </button>
            )}
          </div>
          {loading && <div className="panel"><div className="panel-body">Loading integrations…</div></div>}
          {error && <div className="auth-error" data-testid="settings-error">{error}</div>}
          {!loading && !error && integrations.map((i) => (
            <IntegrationRow
              key={i.key}
              integration={i}
              canManage={canManage}
              onSave={saveIntegration}
              onClear={clearIntegration}
              onRemoveCustom={confirmRemoveCustom}
            />
          ))}
          {!canManage && !loading && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '4px 0' }}>
              You're viewing integrations in read-only mode. Contact an Admin or Owner to rotate keys.
            </div>
          )}

          {/* Inline confirmation for destructive removals (iframe-safe — no window.confirm) */}
          {pendingRemove && (
            <div className="panel" style={{ marginTop: 8, borderColor: 'var(--danger, #dc2626)' }}
                 data-testid="integration-remove-confirm">
              <div className="panel-body" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13 }}>
                  Remove custom integration <strong>{pendingRemove.label}</strong> and its stored value?
                </span>
                <button className="btn btn-primary btn-sm" style={{ background: 'var(--danger)' }}
                        onClick={executeRemoveCustom}
                        data-testid="integration-remove-confirm-btn">
                  Yes, remove
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => setPendingRemove(null)}
                        data-testid="integration-remove-cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add Integration dialog — inline, not a modal (iframe-safe) */}
        {showAdd && (
          <div className="panel" style={{ marginBottom: 24 }} data-testid="integration-add-dialog">
            <div className="panel-header">Add integration</div>
            <div className="panel-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Label *
                <input type="text" value={addForm.label}
                       onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                       placeholder="Resend Email"
                       data-testid="integration-add-label"
                       style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
              </label>
              <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Key (env name) *
                <input type="text" value={addForm.key}
                       onChange={(e) => setAddForm({ ...addForm, key: e.target.value.toUpperCase() })}
                       placeholder="RESEND_API_KEY"
                       data-testid="integration-add-key"
                       style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4, fontFamily: "'IBM Plex Mono', monospace" }} />
              </label>
              <label style={{ gridColumn: 'span 2', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Description
                <input type="text" value={addForm.description}
                       onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                       placeholder="Short summary of what this key unlocks"
                       data-testid="integration-add-description"
                       style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
              </label>
              <label style={{ gridColumn: 'span 2', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Value (optional — leave blank to create the slot and supply the value later)
                <input type="password" value={addForm.value}
                       onChange={(e) => setAddForm({ ...addForm, value: e.target.value })}
                       data-testid="integration-add-value"
                       style={{ width: '100%', padding: 6, marginTop: 2, border: '1px solid var(--border)', borderRadius: 4 }} />
              </label>
              {addError && (
                <div className="auth-error" style={{ gridColumn: 'span 2' }} data-testid="integration-add-error">
                  {addError}
                </div>
              )}
              <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={submitAdd} disabled={addBusy}
                        data-testid="integration-add-submit">
                  {addBusy ? 'Adding…' : 'Add integration'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => { setShowAdd(false); resetAdd(); }}
                        data-testid="integration-add-cancel">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
